(() => {
  const DAY = 86400000;
  const VERSION = 'aven-scratch:v1';
  const memory = new Map();
  const visits = new Set();
  const REVEAL_COVERAGE = 0.65;

  function scratchRadius(width) {
    return Math.max(14, Math.min(20, width * 0.055));
  }

  function launcherPosition({ right, bottom, width, height }) {
    return { right: Math.max(4, right + (width - 56) / 2), bottom: bottom + height + 10 };
  }

  function stickyClearance(top, viewportHeight) {
    return Number.isFinite(top) && top >= 0 && top < viewportHeight ? Math.ceil(viewportHeight - top + 15) : 0;
  }

  function storageKey(campaign) {
    return `${VERSION}:${String(campaign || 'aven10-v1')}`;
  }

  function excludedPath(pathname) {
    return /(?:^|\/)(?:cart|checkout|checkouts)(?:\/|$)/i.test(String(pathname || '').split(/[?#]/)[0]);
  }

  function discountHref(code, location) {
    let path = String(location.pathname || '/');
    if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\') || excludedPath(path)) path = '/';
    const redirect = path + (path === location.pathname ? String(location.search || '') + String(location.hash || '') : '');
    return `/discount/${encodeURIComponent(String(code || '').trim())}?redirect=${encodeURIComponent(redirect)}`;
  }

  function parseState(raw, now) {
    try {
      const state = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!state || !Number.isFinite(state.until) || state.until <= now) return null;
      return { until: state.until, revealed: state.revealed === true };
    } catch (_) {
      return null;
    }
  }

  function canAutoOpen(state) {
    return !state.designMode && !state.seen && !state.dismissed &&
      state.elapsed >= state.delay && state.now >= state.deferUntil && !state.blocked;
  }

  function createCoverage(columns = 32, rows = 18) {
    return { columns, rows, cells: new Uint8Array(columns * rows), count: 0 };
  }

  function scratchCoverage(grid, from, to, width, height, radius) {
    const x1 = from.x * width;
    const y1 = from.y * height;
    const dx = (to.x - from.x) * width;
    const dy = (to.y - from.y) * height;
    const lengthSquared = dx * dx + dy * dy;
    for (let row = 0; row < grid.rows; row += 1) {
      for (let column = 0; column < grid.columns; column += 1) {
        const index = row * grid.columns + column;
        if (grid.cells[index]) continue;
        const x = ((column + 0.5) / grid.columns) * width;
        const y = ((row + 0.5) / grid.rows) * height;
        const t = lengthSquared ? Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / lengthSquared)) : 0;
        if ((x - x1 - t * dx) ** 2 + (y - y1 - t * dy) ** 2 <= radius ** 2) {
          grid.cells[index] = 1;
          grid.count += 1;
        }
      }
    }
    return grid.count / grid.cells.length;
  }

  const helpers = { storageKey, excludedPath, discountHref, parseState, canAutoOpen, createCoverage, scratchCoverage, scratchRadius, REVEAL_COVERAGE, launcherPosition, stickyClearance };
  if (typeof module !== 'undefined' && module.exports) module.exports = helpers;
  if (typeof window === 'undefined' || typeof customElements === 'undefined' || customElements.get('aven-scratch-coupon')) return;

  class AvenScratchCoupon extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.mountFrame = requestAnimationFrame(() => this.mount());
    }

    mount() {
      if (!this.connected || !this.isConnected || this.mounted) return;
      this.launcher = this.querySelector('[data-scratch-open]');
      this.dialog = this.querySelector('[data-scratch-dialog]');
      this.canvas = this.querySelector('[data-scratch-canvas]');
      this.surface = this.querySelector('[data-scratch-surface]');
      this.result = this.querySelector('[data-scratch-result]');
      this.revealButton = this.querySelector('[data-scratch-reveal]');
      this.applyLink = this.querySelector('[data-scratch-apply]');
      this.status = this.querySelector('[data-scratch-status]');
      if (!this.launcher || !this.dialog || !this.canvas || !this.surface || !this.result || !this.revealButton || !this.applyLink || typeof this.dialog.showModal !== 'function') return;
      this.mounted = true;
      this.abort = new AbortController();
      this.key = storageKey(this.dataset.campaign);
      this.designMode = this.dataset.designMode === 'true' || Boolean(window.Shopify && window.Shopify.designMode);
      this.started = Date.now();
      this.delay = Math.max(8, Number(this.dataset.delay) || 8) * 1000;
      this.dismissDays = Math.max(1, Math.min(30, Number(this.dataset.dismissDays) || 7));
      this.deferUntil = this.started;
      this.coverage = createCoverage();
      this.strokes = [];
      this.revealed = false;
      this.pointer = null;
      this.readSaved();
      if (this.dataset.revealed === 'true') this.reveal(false);
      this.updateHref();
      this.listen(this.launcher, 'click', () => this.open(false));
      this.querySelectorAll('[data-scratch-close]').forEach(button => this.listen(button, 'click', () => this.dialog.close()));
      this.listen(this.revealButton, 'click', () => this.reveal(true));
      this.listen(this.dialog, 'close', () => this.onClose());
      this.listen(this.dialog, 'pointerdown', event => {
        const rect = this.dialog.getBoundingClientRect();
        this.backdropDown = event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
      });
      this.listen(this.dialog, 'click', event => {
        if (this.backdropDown && event.target === this.dialog) this.dialog.close();
        this.backdropDown = false;
      });
      this.listen(this.applyLink, 'click', () => {
        this.updateHref();
        this.remember(true);
      });
      this.listen(this.canvas, 'pointerdown', event => this.startScratch(event));
      this.listen(this.canvas, 'pointermove', event => this.moveScratch(event), { passive: false });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(name => this.listen(this.canvas, name, event => this.endScratch(event)));
      this.listen(document, 'pointerdown', event => this.interaction(event));
      this.listen(document, 'pointerup', event => this.interaction(event));
      this.listen(document, 'keydown', event => this.interaction(event));
      this.listen(document, 'focusin', () => {
        this.deferUntil = Date.now() + 1800;
        this.schedule();
      });
      this.listen(document, 'focusout', () => this.schedule());
      ['transitionend', 'transitioncancel', 'animationend', 'animationcancel'].forEach(name => this.listen(document, name, event => {
        if (!this.contains(event.target)) this.schedule();
      }));
      this.listen(window, 'scroll', () => {
        this.deferUntil = Date.now() + 1000;
        this.schedule();
      }, { passive: true });
      this.listen(document, 'visibilitychange', () => this.schedule());
      this.listen(window, 'resize', () => { this.schedulePaint(); this.schedule(); }, { passive: true });
      this.listen(window, 'pageshow', () => {
        this.readSaved();
        this.updateHref();
        this.schedulePaint();
        this.schedule();
      });
      this.listen(window, 'storage', event => {
        if (event.key === this.key) {
          this.readSaved();
          this.schedule();
        }
      });
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'open', 'hidden', 'aria-hidden', 'aria-modal'] });
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.schedulePaint());
        this.resizeObserver.observe(this.surface);
      }
      this.dataset.ready = 'true';
      this.schedule();
    }

    listen(target, event, callback, options = {}) {
      target.addEventListener(event, callback, { ...options, signal: this.abort.signal });
    }

    readSaved() {
      if (this.designMode) {
        this.saved = null;
        this.seen = false;
        return;
      }
      let saved = memory.get(this.key);
      try { saved = window.localStorage.getItem(this.key) || saved; } catch (_) {}
      this.saved = parseState(saved, Date.now());
      this.seen = visits.has(this.key);
      try { this.seen = this.seen || window.sessionStorage.getItem(`${this.key}:visit`) === '1'; } catch (_) {}
      if (this.saved && this.saved.revealed) this.reveal(false);
    }

    remember(revealed = this.revealed) {
      if (this.designMode) return;
      this.saved = { until: Date.now() + this.dismissDays * DAY, revealed: Boolean(revealed) };
      memory.set(this.key, this.saved);
      try { window.localStorage.setItem(this.key, JSON.stringify(this.saved)); } catch (_) {}
      this.markSeen();
    }

    markSeen() {
      if (this.designMode) return;
      this.seen = true;
      visits.add(this.key);
      try { window.sessionStorage.setItem(`${this.key}:visit`, '1'); } catch (_) {}
    }

    updateHref() {
      this.applyLink.href = discountHref(this.dataset.code, window.location);
    }

    visible(element) {
      if (!element || element.hidden || element.getAttribute('aria-hidden') === 'true' || !element.getClientRects().length) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    }

    otherOverlay() {
      const selectors = 'cart-drawer.active, #CartDrawer.active, cart-notification.active, dialog[open], [role="dialog"], [aria-modal="true"], details[open].menu-drawer-container, #shopify-pc__banner, .shopify-pc__banner__dialog, [data-cookie-banner], [data-consent-dialog], #onetrust-banner-sdk';
      return Array.from(document.querySelectorAll(selectors)).some(element => !this.contains(element) && this.visible(element));
    }

    inputActive() {
      const active = document.activeElement;
      return Boolean(active && active.matches('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"]'));
    }

    interaction(event) {
      if (!event.isTrusted || this.contains(event.target)) return;
      const target = event.target && event.target.closest ? event.target : null;
      const sensitive = target && target.closest('form, a, button, input, textarea, select, [contenteditable], cart-drawer, [data-cart-toggle], [aria-controls="CartDrawer"]');
      this.deferUntil = Date.now() + (sensitive ? 2500 : 1400);
      this.schedule();
    }

    schedule() {
      if (!this.connected || !this.mounted || this.checkFrame) return;
      this.checkFrame = requestAnimationFrame(() => {
        this.checkFrame = null;
        this.check();
      });
    }

    check() {
      clearTimeout(this.autoTimer);
      if (!this.connected) return;
      this.positionLauncher();
      const excluded = excludedPath(window.location.pathname);
      const overlay = this.otherOverlay();
      if (this.dialog.open && (excluded || overlay)) {
        this.dialog.close();
        return;
      }
      const hide = excluded || overlay || this.dialog.open;
      if (this.launcher.hidden !== hide) this.launcher.hidden = hide;
      if (this.dialog.open || this.designMode || excluded || this.seen || this.saved) return;
      const now = Date.now();
      const blocked = overlay || this.inputActive() || document.visibilityState === 'hidden';
      if (canAutoOpen({ designMode: this.designMode, seen: this.seen, dismissed: Boolean(this.saved), elapsed: now - this.started, delay: this.delay, now, deferUntil: this.deferUntil, blocked })) {
        this.open(true);
        return;
      }
      const due = Math.max(this.started + this.delay, this.deferUntil);
      if (!blocked && due > now) this.autoTimer = setTimeout(() => this.schedule(), due - now + 30);
    }

    positionLauncher() {
      const anchor = document.querySelector('.scroll-to-top-btn-bottom-right');
      if (!anchor) return;
      this.dockAnchor = anchor;
      const sticky = document.querySelector('sticky-atc.sticky-atc');
      const clearance = sticky && this.visible(sticky) ? stickyClearance(sticky.getBoundingClientRect().top, window.innerHeight) : 0;
      const dockValue = `${clearance}px`;
      if (anchor.style.getPropertyValue('--scratch-dock-clearance') !== dockValue) anchor.style.setProperty('--scratch-dock-clearance', dockValue);
      const style = window.getComputedStyle(anchor);
      const right = parseFloat(style.right);
      const bottom = parseFloat(style.bottom);
      if (!Number.isFinite(right) || !Number.isFinite(bottom)) return;
      const position = launcherPosition({ right, bottom, width: parseFloat(style.width) || 40, height: parseFloat(style.height) || 40 });
      Object.entries(position).forEach(([property, value]) => {
        const name = `--scratch-${property}`;
        const next = `${value}px`;
        if (this.launcher.style.getPropertyValue(name) !== next) this.launcher.style.setProperty(name, next);
      });
    }

    open(automatic) {
      if (!this.connected || this.dialog.open || excludedPath(window.location.pathname) || this.otherOverlay()) return;
      if (automatic && (this.inputActive() || document.visibilityState === 'hidden')) return;
      this.previousFocus = document.activeElement;
      try { this.dialog.showModal(); } catch (_) { return; }
      this.launcher.setAttribute('aria-expanded', 'true');
      this.markSeen();
      this.schedulePaint();
      this.schedule();
    }

    onClose() {
      this.endScratch();
      this.remember();
      this.launcher.setAttribute('aria-expanded', 'false');
      this.check();
      const target = this.previousFocus && this.previousFocus.isConnected && this.previousFocus !== document.body ? this.previousFocus : this.launcher;
      if (!this.otherOverlay() && this.visible(target) && typeof target.focus === 'function') target.focus({ preventScroll: true });
      this.previousFocus = null;
    }

    schedulePaint() {
      if (!this.connected || !this.dialog.open || this.revealed || this.paintFrame) return;
      this.paintFrame = requestAnimationFrame(() => {
        this.paintFrame = null;
        this.paint();
      });
    }

    paint() {
      if (!this.connected || !this.dialog.open || this.revealed) return;
      const rect = this.surface.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const width = rect.width;
      const height = rect.height;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(width * ratio);
      this.canvas.height = Math.round(height * ratio);
      const context = this.canvas.getContext('2d');
      if (!context) return;
      this.context = context;
      this.canvasSize = { width, height };
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.globalCompositeOperation = 'source-over';
      const gradient = context.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#f0dfe1');
      gradient.addColorStop(0.48, '#ead0d3');
      gradient.addColorStop(0.72, '#f8ebe9');
      gradient.addColorStop(1, '#ddbbc0');
      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);
      for (let index = 0; index < 110; index += 1) {
        const x = ((Math.sin(index * 127.1 + 17) + 1) / 2) * width;
        const y = ((Math.sin(index * 311.7 + 41) + 1) / 2) * height;
        context.fillStyle = index % 3 ? 'rgba(106,6,15,0.055)' : 'rgba(255,255,255,0.65)';
        context.beginPath();
        context.arc(x, y, index % 3 ? 0.65 : 1.25, 0, Math.PI * 2);
        context.fill();
      }
      context.strokeStyle = 'rgba(106,6,15,0.12)';
      context.lineWidth = 1;
      context.strokeRect(10.5, 10.5, width - 21, height - 21);
      [[28, 28], [width - 28, height - 28]].forEach(([x, y]) => {
        context.beginPath();
        context.moveTo(x, y - 7);
        context.lineTo(x + 4, y);
        context.lineTo(x, y + 7);
        context.lineTo(x - 4, y);
        context.closePath();
        context.stroke();
      });
      context.fillStyle = '#6a060f';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.font = `${Math.min(29, width * 0.085)}px Georgia, serif`;
      context.fillText(this.dataset.scratchTitle || '', width / 2, height / 2 - 12, width - 48);
      context.font = '12px Arial, sans-serif';
      context.fillText(this.dataset.scratchHint || '', width / 2, height / 2 + 22, width - 40);
      this.strokes.forEach(stroke => this.erase(stroke));
    }

    point(event) {
      const rect = this.canvas.getBoundingClientRect();
      return { x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)), y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)) };
    }

    startScratch(event) {
      if (this.revealed || this.pointer !== null || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (!this.context) this.paint();
      if (!this.context) return;
      this.pointer = event.pointerId;
      this.lastPoint = this.point(event);
      try { this.canvas.setPointerCapture(event.pointerId); } catch (_) {}
      this.addStroke(this.lastPoint, this.lastPoint);
    }

    moveScratch(event) {
      if (event.pointerId !== this.pointer || this.revealed) return;
      event.preventDefault();
      const next = this.point(event);
      this.addStroke(this.lastPoint, next);
      this.lastPoint = next;
    }

    endScratch(event) {
      if (event && event.pointerId !== this.pointer) return;
      const pointer = this.pointer;
      this.pointer = null;
      if (typeof pointer === 'number' && this.canvas && this.canvas.hasPointerCapture(pointer)) this.canvas.releasePointerCapture(pointer);
      this.lastPoint = null;
    }

    addStroke(from, to) {
      const { width, height } = this.canvasSize;
      const radius = scratchRadius(width);
      const stroke = { from, to, radius: radius / width };
      this.strokes.push(stroke);
      this.erase(stroke);
      if (scratchCoverage(this.coverage, from, to, width, height, radius) >= REVEAL_COVERAGE) this.reveal(true);
    }

    erase(stroke) {
      const { width, height } = this.canvasSize;
      const context = this.context;
      context.globalCompositeOperation = 'destination-out';
      context.lineWidth = stroke.radius * width * 2;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.beginPath();
      context.moveTo(stroke.from.x * width, stroke.from.y * height);
      context.lineTo(stroke.to.x * width, stroke.to.y * height);
      context.stroke();
      context.beginPath();
      context.arc(stroke.to.x * width, stroke.to.y * height, stroke.radius * width, 0, Math.PI * 2);
      context.fill();
    }

    reveal(announce) {
      if (this.revealed) return;
      this.revealed = true;
      this.endScratch();
      this.dataset.revealed = 'true';
      this.canvas.hidden = true;
      this.result.hidden = false;
      const heading = this.querySelector('[data-scratch-heading]');
      const intro = this.querySelector('[data-scratch-intro]');
      if (heading && this.dataset.revealedHeading) heading.textContent = this.dataset.revealedHeading;
      if (intro && this.dataset.revealedIntro) intro.textContent = this.dataset.revealedIntro;
      this.querySelectorAll('[data-scratch-before]').forEach(element => { element.hidden = true; });
      this.revealButton.hidden = true;
      const label = this.launcher.querySelector('[data-scratch-launcher-label]') || this.launcher;
      if (this.dataset.launcherClaimed) label.textContent = this.dataset.launcherClaimed;
      if (announce) {
        this.remember(true);
        if (this.status) this.status.textContent = this.dataset.revealedMessage || '';
        if (this.dialog.open) this.applyLink.focus({ preventScroll: true });
      }
    }

    disconnectedCallback() {
      this.connected = false;
      this.mounted = false;
      if (this.abort) this.abort.abort();
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.dockAnchor) this.dockAnchor.style.removeProperty('--scratch-dock-clearance');
      clearTimeout(this.autoTimer);
      [this.mountFrame, this.checkFrame, this.paintFrame].forEach(frame => cancelAnimationFrame(frame));
      this.mountFrame = this.checkFrame = this.paintFrame = null;
      this.endScratch();
      if (this.dialog && this.dialog.open) this.dialog.close();
    }
  }

  customElements.define('aven-scratch-coupon', AvenScratchCoupon);
})();
